import { message } from 'antd';
import { axiosInstance } from './Api';
import {
  IAdvancedAnalyticsApiPayload,
  IPreprocessedAnalyticsPayload,
  IScatterTableApiPayload,
} from './../typings/chart.d';
import { wrapCoordinates } from '../components/Map/MapHelper';
import { IResult } from '../typings/api';

export default class ChartRequest {
  private endPoint = 'Chart';

  // This API returns only the time series/scatter plot result.
  // Meant to be called for first best wave dataset with 1 year time range only
  // (last year of complete time range returned from previous GetMetadata call).
  async createBasicChartPreview(
    datasetName: string,
    startDate: string,
    endDate: string,
    coordinates: number[],
    isFwe?: boolean,
    pointName?: string
  ) {
    const [longitude, latitude] = wrapCoordinates(coordinates);
    try {
      // always call without userId & key, in order to be consistent when user logs in after this call
      const r = await axiosInstance.post(
        `/${this.endPoint}/CreateBasicChartPreview`,
        {
          datasetName: `${datasetName}`,
          fileFormat: 'csv',
          location: `${longitude}, ${latitude}`,
          startDate: `${startDate}`,
          endDate: `${endDate}`,
          verticalLevel: null,
          isFwe: isFwe,
          pointName: pointName
        }
      );
      return r.data;
    } catch (error) {
      return { Result: null, Error: error };
    }
  }

  // This API returns only the rose plot result.
  // Meant to be called for first best wave dataset with full time range
  // (time range is returned from previous GetMetadata call).
  async createRosePlotPreview(
    datasetName: string,
    startDate: string,
    endDate: string,
    coordinates: number[],
    isFwe?: boolean,
    pointName?: string
  ) {
    const [longitude, latitude] = wrapCoordinates(coordinates);
    // always call without userId & key, in order to be consistent when user logs in after this call
    const r = await axiosInstance.post(
      `/${this.endPoint}/CreateRosePlotPreview`,
      {
        datasetName: `${datasetName}`,
        fileFormat: 'csv',
        location: `${longitude}, ${latitude}`,
        startDate: `${startDate}`,
        endDate: `${endDate}`,
        verticalLevel: null,
        isFwe: isFwe,
        pointName: pointName
      }
    );
    return r.data.Result;
  }

  async getScatterTable(payload: IScatterTableApiPayload) {
    try {
      const response = await axiosInstance.post(
        `/${this.endPoint}/GetScatterTable`,
        payload
      );
      return response.data;
    } catch (error) {
      return { Result: null, Error: error };
    }
  }

  async getAdvancedAnalytics(payload: IAdvancedAnalyticsApiPayload) {
    try {
      const response = await axiosInstance.post(
        `/${this.endPoint}/GetAdvancedAnalytics`,
        { ...payload }
      );
      return response.data;
    } catch (error) {
      return { Result: null, Error: error };
    }
  }

  async getPreprocessedAnalytics(payload: IPreprocessedAnalyticsPayload) {
    try {
      const response = await axiosInstance.post(
        `/${this.endPoint}/PreProcessedAnalytics`,
        payload
      );
      return response.data;
    } catch (error) {
      return { Result: null, Error: error };
    }

    /* {
   "Latitude": "40.950430",
    "Longitude": "-70.628013",
    "DatasetId":"waterdata-dataset-ParkCityWind_SW_CFSR",
    "AnalyticsType": "XLS"

} */
  }

  // This API returns updated time series result.
  // You can call each of them after calling “CreateFullExtractionTasks” and get the orderId + requestId.
  // You can either wait for the full extraction status is completed for that requestId,
  // or even before that since I added a checking of extraction status and a time out = 2 mins, for each API.
  async getBasicChartV2(
    orderId: string,
    requestId: string,
    datasetName: string,
    parameters: string,
    startDate: string,
    endDate: string,
    month?: number,
    isFwe?: boolean
  ) {
    try {
      const url =
        `/${this.endPoint}/GetBasicCharts?orderId=${orderId}&requestId=${requestId}&datasetname=${datasetName}&parameters=` +
        encodeURIComponent(parameters) +
        `&start=` +
        encodeURIComponent(startDate) +
        `&end=` +
        encodeURIComponent(endDate);
      const completeUrl = month ? url + `&month=${month}` : url;
      const enrichedUrl = isFwe ? url + `&isFwe=${isFwe}` : completeUrl;
      const r = await axiosInstance.get(enrichedUrl);
      return r.data;
    } catch (error) {
      message.error(JSON.stringify(error));
      console.log(error);
    }
  }

  // This API returns updated cross-dataset scatter plot result.
  // The API requires another set of items for dataset 2 to compare.
  async getJointScatterPlotV2(
    orderId: string,
    requestId: string,
    datasetName: string,
    parameters: string,
    orderId2: string,
    requestId2: string,
    datasetName2: string,
    parameters2: string,
    startDate: string,
    endDate: string,
    month?: number,
    isFwe?: boolean
  ) {
    try {
      const url =
        `/${this.endPoint}/GetJointScatterPlot?orderId=${orderId}&requestId=${requestId}&datasetname=${datasetName}&parameters=` +
        encodeURIComponent(parameters) +
        `&orderId2=${orderId2}&requestId2=${requestId2}&datasetname2=${datasetName2}&parameters2=` +
        encodeURIComponent(parameters2) +
        `&start=` +
        encodeURIComponent(startDate) +
        `&end=` +
        encodeURIComponent(endDate);
      const completeUrl = month ? url + `&month=${month}` : url;
      const enrichedUrl = isFwe ? url + `&isFwe=${isFwe}` : completeUrl;
      const r = await axiosInstance.get(enrichedUrl);
      return r.data;
    } catch (error) {
      message.error(JSON.stringify(error));
      console.log(error);
    }
  }

  // This API returns updated time series result.
  // You can call each of them after calling “CreateFullExtractionTasks” and get the orderId + requestId.
  // You can either wait for the full extraction status is completed for that requestId,
  // or even before that since I added a checking of extraction status and a time out = 2 mins, for each API.
  async getRosePlotV2(
    orderId: string,
    requestId: string,
    datasetName: string,
    startDate: string,
    endDate: string,
    month?: number,
    degreeInterval?: number,
    valueColumnName?: string,
    directionColumnName?: string,
    legendInterval?: number,
    isFwe?: boolean
  ) {
    try {
      const url =
        `/${this.endPoint}/GetRosePlot?orderId=${orderId}&requestId=${requestId}&datasetname=${datasetName}&start=` +
        encodeURIComponent(startDate) +
        `&end=` +
        encodeURIComponent(endDate);
      const urlMonth = month ? url + `&month=${month}` : url;
      const urlDegreeInterval = degreeInterval
        ? urlMonth + `&degreeInterval=${degreeInterval}`
        : urlMonth;
      const urlValueColumnName = valueColumnName
        ? urlDegreeInterval +
          `&valueColumnName=` +
          encodeURIComponent(valueColumnName)
        : urlDegreeInterval;
      const urlDirectionColumnName = directionColumnName
        ? urlValueColumnName +
          `&directionColumnName=` +
          encodeURIComponent(directionColumnName)
        : urlValueColumnName;
      const completeUrl = legendInterval
        ? urlDirectionColumnName + `&legendInterval=${legendInterval}`
        : urlDirectionColumnName;
      const enrichedUrl = isFwe ? completeUrl + `&isFwe=${isFwe}` : completeUrl;
      const r = await axiosInstance.get(enrichedUrl);
      return r.data;
    } catch (error) {
      message.error(JSON.stringify(error));
      console.log(error);
    }
  }

  async getTimeseriesInsitu(
    idName: string,
    orderId: string,
    parameterName: string,
    startDate: string,
    endDate: string,
  ) {
    try {
      const url =
        `/InSitu/GetTimeSeries?orderId=${orderId}&idName=${idName}&parameterName=` +
        encodeURIComponent(parameterName) +
        `&startDate=` +
        encodeURIComponent(startDate) +
        `&endDate=` +
        encodeURIComponent(endDate);
      const r = await axiosInstance.get(url);
      return r.data;
    } catch (error) {
      message.error(JSON.stringify(error));
      console.log(error);
    }
  }

  async getScatterPlotInsitu(
    idName: string,
    orderId: string,
    parameterName: string,
    startDate: string,
    endDate: string,
  ) {
    try {
      const url =
        `/InSitu/getScatterPlot?orderId=${orderId}&idName=${idName}&parameterName=` +
        encodeURIComponent(parameterName) +
        `&startDate=` +
        encodeURIComponent(startDate) +
        `&endDate=` +
        encodeURIComponent(endDate);
      const r = await axiosInstance.get(url);
      return r.data
    } catch (error) {
      message.error(JSON.stringify(error));
      console.log(error);
    }
  }

  async getRosePlotInsitu(
    idName: string,
    orderId: string,
    numberOfDirection: number,
    parameterName: string,
    startDate: string,
    endDate: string,
  ) {
    try {
      const url =
        `/InSitu/getRosePlot?orderId=${orderId}&idName=${idName}&numberOfDirection=${numberOfDirection}&parameterName=` +
        encodeURIComponent(parameterName) +
        `&start=` +
        encodeURIComponent(startDate) +
        `&end=` +
        encodeURIComponent(endDate);
      const r = await axiosInstance.get(url);
      return r.data
    } catch (error: any) {
      message.error(JSON.stringify(error.message));
      console.log(error);
    }
  }
}
